import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { ChevronLeft, ChevronRight, Phone, Mail, MessageCircle, MapPin } from "lucide-react";

const SUPPORT_PHONE = "+966540898859";
const SUPPORT_EMAIL = "support@jayek.app";
const WHATSAPP_NUMBER = "966540898859";

export default function ContactUs() {
  const navigate = useNavigate();
  const { lang, isRTL } = useLang();
  const Chevron = isRTL ? ChevronRight : ChevronLeft;

  const channels = [
    {
      icon: Phone,
      title: lang === "ar" ? "اتصال هاتفي" : "Phone",
      subtitle: SUPPORT_PHONE,
      color: "#0086C9",
      bg: "#E0F2FE",
      href: `tel:${SUPPORT_PHONE}`,
      testId: "contact-phone",
    },
    {
      icon: MessageCircle,
      title: lang === "ar" ? "واتساب" : "WhatsApp",
      subtitle: lang === "ar" ? "ردّ سريع خلال دقائق" : "Quick reply within minutes",
      color: "#1DA851",
      bg: "#E8F7EE",
      href: `https://wa.me/${WHATSAPP_NUMBER}`,
      testId: "contact-whatsapp",
    },
    {
      icon: Mail,
      title: lang === "ar" ? "البريد الإلكتروني" : "Email",
      subtitle: SUPPORT_EMAIL,
      color: "#E05D3A",
      bg: "#FEF0EE",
      href: `mailto:${SUPPORT_EMAIL}`,
      testId: "contact-email",
    },
  ];

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen pb-20">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#F2F2F2] px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="contact-back" aria-label="back">
          <Chevron className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
        </button>
        <h1 className="font-bold text-lg text-[#1A1A1A]">{lang === "ar" ? "اتصل بنا" : "Contact us"}</h1>
      </div>

      <div className="px-5 pt-6">
        {/* Hero */}
        <div className="rounded-3xl p-6 mb-6 text-white" style={{ background: "linear-gradient(135deg, #E05D3A 0%, #C24B2C 100%)" }}>
          <h2 className="font-display text-2xl font-black mb-2">
            {lang === "ar" ? "نحن هنا لخدمتك" : "We're here to help"}
          </h2>
          <p className="text-white/85 text-sm leading-relaxed">
            {lang === "ar"
              ? "فريق دعم جايك جاهز للإجابة على استفساراتك ومساعدتك في أي وقت."
              : "Jayek support team is ready to answer your questions anytime."}
          </p>
        </div>

        {/* Channels */}
        <p className="text-xs font-bold text-[#8B8B8B] mb-3 uppercase tracking-wider">
          {lang === "ar" ? "تواصل معنا عبر" : "Reach us via"}
        </p>
        <div className="space-y-3 mb-8">
          {channels.map(({ icon: Icon, title, subtitle, color, bg, href, testId }) => (
            <a
              key={testId}
              href={href}
              target={testId === "contact-whatsapp" ? "_blank" : undefined}
              rel={testId === "contact-whatsapp" ? "noreferrer" : undefined}
              className="flex items-center gap-4 bg-white border border-[#F2F2F2] rounded-2xl p-4 active:bg-[#FAFAFA] transition-colors"
              data-testid={testId}
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
                <Icon className="w-6 h-6" style={{ color }} strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[15px] text-[#1A1A1A]">{title}</p>
                <p className="text-xs text-[#6B6B6B] truncate" dir={testId === "contact-phone" || testId === "contact-email" ? "ltr" : "auto"}>
                  {subtitle}
                </p>
              </div>
              <Chevron className="w-5 h-5 text-[#CCCCCC]" />
            </a>
          ))}
        </div>

        {/* Address */}
        <p className="text-xs font-bold text-[#8B8B8B] mb-3 uppercase tracking-wider">
          {lang === "ar" ? "موقعنا" : "Our location"}
        </p>
        <div className="flex items-center gap-3 bg-[#FAFAFA] rounded-2xl p-4">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
            <MapPin className="w-5 h-5 text-[#E05D3A]" strokeWidth={2.2} />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm text-[#1A1A1A]">
              {lang === "ar" ? "بحرة، المملكة العربية السعودية" : "Bahrah, Saudi Arabia"}
            </p>
            <p className="text-xs text-[#6B6B6B]">
              {lang === "ar" ? "نطاق الخدمة الحالي" : "Current service area"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
